import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CustomerStorage, AdminStorage } from '../utils/storage';
import { isCustomerRoute, fireCustomerSessionExpired } from '../utils/sessionExpired';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Refresh `BUFFER_SECONDS` before backend access-token expiry.
// Backend currently issues 60-minute access tokens; the exact value comes
// down in the login/refresh response (`expires_in`, in seconds), so this
// component is self-correcting if backend TTL ever changes.
const REFRESH_BUFFER_SECONDS = 90;
const FALLBACK_EXPIRES_IN = 60 * 60; // 60 min, matches backend default
// If a customer returns to the tab with less than this remaining on the
// access token, we trigger a proactive refresh on visibility change rather
// than waiting for the in-flight request to 401.
const PROACTIVE_REFRESH_IF_REMAINING_LT = 5 * 60; // 5 min

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  // Single timer handle. setTimeout (not setInterval) — re-armed after each
  // successful refresh. Survives tab throttling because the timer is rearmed
  // on every focus event with a fresh delta.
  const refreshTimerRef = useRef(null);
  // Wall-clock timestamp (ms since epoch) of the *current* access token's
  // expiry. Stored in a ref because the visibility-change handler reads
  // it without re-creating the listener.
  const tokenExpiresAtRef = useRef(0);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Tokens live in httpOnly cookies now — the browser attaches them
  // automatically (axios.defaults.withCredentials = true, set in App.js).
  // No token is read or written here; the backend rotates the cookies via
  // Set-Cookie on this same call.
  // Circular by design: performRefresh schedules the next refresh via
  // scheduleRefresh, and scheduleRefresh's timer calls performRefresh.
  // Routing through a ref (updated by the effect below) breaks the
  // useCallback dependency cycle without any behavior change — both
  // functions still always call the *latest* version of the other.
  const scheduleRefreshRef = useRef(() => {});

  const performRefresh = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API}/auth/refresh`,
        {},
        { _skipAuthRefresh: true }
      );
      const expiresIn = response.data.expires_in || FALLBACK_EXPIRES_IN;
      tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
      scheduleRefreshRef.current(expiresIn);
      return true;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        CustomerStorage.clearAuthOnly();
        tokenExpiresAtRef.current = 0;
        if (isCustomerRoute()) {
          fireCustomerSessionExpired(null);
        }
      }
      // eslint-disable-next-line no-console
      console.warn('Silent token refresh failed:', status);
      return false;
    }
  }, []);

  const scheduleRefresh = useCallback((expiresInSeconds) => {
    clearRefreshTimer();
    const delay = Math.max(
      30,                                      // never less than 30s
      (expiresInSeconds - REFRESH_BUFFER_SECONDS)
    ) * 1000;
    refreshTimerRef.current = setTimeout(() => {
      performRefresh();
    }, delay);
  }, [clearRefreshTimer, performRefresh]);

  useEffect(() => {
    scheduleRefreshRef.current = scheduleRefresh;
  }, [scheduleRefresh]);

  // On mount / tab focus: check token age and proactively refresh if it's
  // close to expiry. Robust to tab throttling that may have killed the
  // setTimeout in the background.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!CustomerStorage.isLoggedIn()) return;
      // Only auto-refresh if we know the token's expiry (i.e., we logged
      // in / refreshed in this tab session). On a fresh page load with a
      // stale cached profile, `tokenExpiresAtRef` is 0 and we let the
      // interceptor handle any organic 401 instead of blasting a refresh.
      if (tokenExpiresAtRef.current === 0) return;
      const remaining = (tokenExpiresAtRef.current - Date.now()) / 1000;
      if (remaining < PROACTIVE_REFRESH_IF_REMAINING_LT) {
        performRefresh();
      }
    };

    // The customer auth interceptor refreshes tokens independently when it
    // sees a 401. Without this event, AuthContext's expiry tracker stays
    // stale (0 on cold load), so it never schedules the proactive refresh.
    // The interceptor dispatches `customer-token-refreshed` with the new
    // expires_in so we can re-arm here.
    const handleInterceptorRefresh = (e) => {
      const expiresIn = (e && e.detail && e.detail.expires_in) || FALLBACK_EXPIRES_IN;
      tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
      scheduleRefresh(expiresIn);

      // Quiet trust signal: show "Session extended" toast at most once
      // every 24h, and only if this rotation isn't happening right after
      // a fresh login.
      try {
        const now = Date.now();
        const lastLoginAt = parseInt(localStorage.getItem('lastLoginAt') || '0', 10);
        const justLoggedIn = lastLoginAt && (now - lastLoginAt) < 60 * 1000; // 1 min grace
        const lastToastAt = parseInt(localStorage.getItem('lastSessionExtendedToastAt') || '0', 10);
        const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
        if (!justLoggedIn && (now - lastToastAt) >= TWENTY_FOUR_HOURS) {
          toast.success('Session extended', {
            duration: 2500,
            description: 'Welcome back — keep going.',
          });
          localStorage.setItem('lastSessionExtendedToastAt', String(now));
        }
      } catch (_) { /* localStorage unavailable — silent */ }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    window.addEventListener('customer-token-refreshed', handleInterceptorRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      window.removeEventListener('customer-token-refreshed', handleInterceptorRefresh);
    };
  }, [performRefresh, scheduleRefresh]);

  // On initial mount: hydrate optimistically from the cached profile (fast,
  // no network round trip). If no cached profile exists, JS can't read the
  // httpOnly access_token cookie itself, so fall back to GET /auth/me to
  // check whether a valid session cookie still exists (e.g. localStorage
  // was cleared but the cookie wasn't).
  useEffect(() => {
    const cachedUser = CustomerStorage.getUser();
    const cachedGuest = CustomerStorage.getGuest();

    if (cachedUser) {
      setUser(cachedUser);
      setIsGuest(false);
      setLoading(false);
      return clearRefreshTimer;
    }
    if (cachedGuest) {
      setUser(cachedGuest);
      setIsGuest(true);
      setLoading(false);
      return clearRefreshTimer;
    }

    // Skip the customer session-restore call entirely on admin routes —
    // an admin-only browser session will always get a 401 here (no
    // customer cookie exists), which is just console noise.
    if (window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return clearRefreshTimer;
    }

    axios.get(`${API}/auth/me`, { _skipAuthRefresh: true })
      .then((res) => {
        if (res.data.is_guest) {
          CustomerStorage.setGuest(res.data.guest);
          setUser(res.data.guest);
          setIsGuest(true);
        } else {
          CustomerStorage.setUser(res.data.user);
          setUser(res.data.user);
          setIsGuest(false);
        }
      })
      .catch(() => { /* no valid session — stay logged out */ })
      .finally(() => setLoading(false));

    return clearRefreshTimer;
  }, [clearRefreshTimer]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });

    // CRITICAL FIX: Clear stale auth before setting new login.
    // Use clearAuthOnly() so bookingIntent / smashzone_selection survive -
    // the whole point of the pre-login intent is to resume after login.
    CustomerStorage.clearAuthOnly();
    clearRefreshTimer();

    // SECURITY: Also clear any admin sessions (if admin didn't log out)
    AdminStorage.clear();

    // Cookies are set server-side; cache the profile for instant UI.
    CustomerStorage.setUser(response.data.user);
    localStorage.setItem('lastLoginAt', String(Date.now()));
    setUser(response.data.user);
    setIsGuest(false);

    const expiresIn = response.data.expires_in || FALLBACK_EXPIRES_IN;
    tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
    scheduleRefresh(expiresIn);

    return response.data;
  };

  const signup = async (name, email, phone, password) => {
    const response = await axios.post(`${API}/auth/signup`, { name, email, phone, password });
    // New flow: signup now requires email verification
    // Backend returns: { message, email, requires_verification: true }
    // DO NOT set user here - wait for email verification
    return response.data;
  };

  const guestLogin = async (name, countryCode, phone, otp) => {
    const response = await axios.post(`${API}/auth/guest`, {
      name,
      country_code: countryCode,
      phone,
      otp
    });

    CustomerStorage.clearAuthOnly();
    clearRefreshTimer();
    AdminStorage.clear();

    CustomerStorage.setGuest(response.data.guest);
    localStorage.setItem('lastLoginAt', String(Date.now()));
    setUser(response.data.guest);
    setIsGuest(true);
    return response.data;
  };

  const requestOTP = async (countryCode, phone) => {
    const response = await axios.post(`${API}/auth/request-otp`, {
      country_code: countryCode,
      phone
    });
    return response.data;
  };

  const logout = async () => {
    try {
      // Revoke refresh token + clear the httpOnly cookies server-side
      // (JS can no longer delete them directly).
      await axios.post(`${API}/auth/logout`, {}).catch(() => {}); // Ignore errors
    } catch (error) {
      // Ignore logout API errors
    }

    CustomerStorage.clear();
    clearRefreshTimer();

    // Use requestAnimationFrame to batch state updates
    // This helps avoid concurrent rendering issues
    requestAnimationFrame(() => {
      setUser(null);
      setIsGuest(false);
    });
  };

  // ── Google OAuth helpers ──────────────────────────────────────────────────
  // Exchange Emergent OAuth session_id for SmashZone user data.
  // Returns raw backend response (requires_phone flag or tokens).
  const googleExchange = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google`, { session_id: sessionId });
    return response.data;
  };

  // Complete Google sign-up by providing phone number.
  const googleComplete = async (sessionId, phone) => {
    const response = await axios.post(`${API}/auth/google/complete`, {
      session_id: sessionId,
      phone,
    });
    return response.data;
  };

  // Apply the profile returned by google auth to state + cache + refresh
  // timer. Cookies are already set server-side by this point.
  const setAuthFromGoogle = (data) => {
    CustomerStorage.clearAuthOnly();
    clearRefreshTimer();
    AdminStorage.clear();
    CustomerStorage.setUser(data.user);
    localStorage.setItem('lastLoginAt', String(Date.now()));
    setUser(data.user);
    setIsGuest(false);
    const expiresIn = data.expires_in || FALLBACK_EXPIRES_IN;
    tokenExpiresAtRef.current = Date.now() + expiresIn * 1000;
    scheduleRefresh(expiresIn);
  };

  // Used right after email verification — the backend already set the
  // auth cookies in that response, so this just adopts the returned user
  // as the authenticated session (no separate login call needed).
  const setAuthenticatedUser = (authedUser) => {
    if (!authedUser) return;
    CustomerStorage.setUser(authedUser);
    setUser(authedUser);
    setIsGuest(false);
  };

  // Used by Profile self-service to push fresh user state app-wide after
  // name / phone updates.
  const updateUser = (updatedUser) => {
    if (!updatedUser) return;
    CustomerStorage.setUser(updatedUser);
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, isGuest, loading, login, signup, guestLogin, requestOTP, logout, updateUser, setAuthenticatedUser, googleExchange, googleComplete, setAuthFromGoogle }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
